import { useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for debounced save operations
 * @param {Function} saveFunction - The function to call for saving
 * @param {number} delay - Debounce delay in milliseconds (default: 2000)
 * @returns {Function} Debounced save function
 */
export const useDebouncedSave = (saveFunction, delay = 2000) => {
  const timeoutRef = useRef(null)

  const debouncedSave = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        saveFunction(...args)
      }, delay)
    },
    [saveFunction, delay]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedSave
}

/**
 * Hook for auto-saving form data
 * @param {Object} data - The data to save
 * @param {Function} saveFunction - The function to call for saving
 * @param {number} delay - Debounce delay in milliseconds
 */
export const useAutoSave = (data, saveFunction, delay = 2000) => {
  const debouncedSave = useDebouncedSave(saveFunction, delay)
  const initialRender = useRef(true)

  useEffect(() => {
    // Skip save on initial render
    if (initialRender.current) {
      initialRender.current = false
      return
    }

    debouncedSave(data)
  }, [data, debouncedSave])
}
